import { chakra, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { MentionBoostCampaignBudgetMode, type MentionBoostCampaignDto } from '@fluenceur/types';
import { Check } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

interface PlacementCampaignRowProps {
  campaign: MentionBoostCampaignDto;
  isSelected: boolean;
  onSelect: (campaign: MentionBoostCampaignDto) => void;
}

/**
 * One selectable campaign: its name, and the three facts that decide how the
 * boost behaves inside it — objective, where the budget lives, and how many ad
 * sets already run there.
 */
export function PlacementCampaignRow({
  campaign,
  isSelected,
  onSelect,
}: PlacementCampaignRowProps) {
  const { t } = useTranslation();

  const subline = [
    t(`mentions.boost.setup.placement.objective.${campaign.objective}`),
    t(
      campaign.budgetMode === MentionBoostCampaignBudgetMode.CAMPAIGN
        ? 'mentions.boost.setup.placement.budgetMode.campaign'
        : 'mentions.boost.setup.placement.budgetMode.adSet'
    ),
    t('mentions.boost.setup.placement.adSetsCount', { count: campaign.adSetsCount }),
  ].join(' · ');

  return (
    <chakra.button
      type="button"
      textAlign="left"
      w="full"
      px={3}
      py={2.5}
      rounded="lg"
      border="1px solid"
      borderColor={isSelected ? 'fg' : 'border.muted'}
      bg={isSelected ? 'bg.subtle' : 'transparent'}
      _hover={{ borderColor: isSelected ? 'fg' : 'border.emphasized' }}
      onClick={() => onSelect(campaign)}
      aria-pressed={isSelected}
    >
      <HStack justify="space-between" gap={3} align="center">
        <VStack align="stretch" gap={0.5} minW={0}>
          <Text fontSize="sm" fontWeight={isSelected ? 'semibold' : 'medium'} truncate>
            {campaign.name}
          </Text>
          <Text fontSize="xs" color="fg.muted" truncate>
            {subline}
          </Text>
        </VStack>
        {isSelected && (
          <Icon size="sm" color="fg" flexShrink={0}>
            <Check />
          </Icon>
        )}
      </HStack>
    </chakra.button>
  );
}
